import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../../icons/registry';
import { COACHES, LEAD_COACH } from '../../../fixtures/coaches';
import { TayroMark } from '../../brand/TayroMark';
import { AccountMenu } from '../../firstPlan/FirstPlanShell.AccountMenu';
import styles from './Step7Welcome.v2.module.css';

/**
 * Part 7 (onboarding_2_0_v1), screen 12 — Meeting your coaches, restyled.
 * Frames 1ah (setting up), 1ai (the reveal, phone) and 1aw (the reveal, 1280)
 * of `.claude/briefs/cd-2.0-part7-onboarding/Part 7 Onboarding.dc.html`.
 *
 * PRESENTATION ONLY. Every piece of state and every handler lives in
 * `Step7Welcome.tsx` (the thinking timers, the completion POST, the one-shot
 * navigate, the flag waits) and arrives as props, so the SP-10 honesty pins and
 * the B108, B149 and B26 behaviour read the same code with the flag off and on.
 * Rendered only behind the flag; with it off `Step7Welcome` returns its own,
 * unchanged tree.
 *
 * The content is the code's, word for word (Meeting your coaches keeps its
 * content). The coach rows take the landing's strings 43 to 57 through the
 * fixture's added `landingRole` and `landingDesc`; nothing here suggests the
 * coaches are people.
 */

type Phase = 'thinking' | 'reveal';
type CtaState = 'idle' | 'saving' | 'error' | 'session_lost';

export type Step7WelcomeV2Props = {
  phase: Phase;
  /** The three ratified thinking lines, in order. */
  lines: readonly string[];
  /** How many rows have settled to done. */
  rowsDone: number;
  /** "{Name}, your" or "Your": the headline's lead, as the code resolves it. */
  headlineLead: string;
  /** The athlete's resolved name, for the account button's initials. */
  accountName: string;
  /** True when a user is signed in; the account button is only drawn then. */
  signedIn: boolean;
  onSignOut: () => void;
  firstMessage: string;
  ctaState: CtaState;
  buttonLabel: string;
  errorMessage: string | null;
  /** The session-lost chip's one action ("Sign in again"), or null on every other chip. */
  sessionLostAction: { label: string; to: string } | null;
  onComplete: () => void;
  onChangeAnswers?: () => void;
  iconsOn: boolean;
};

export function Step7WelcomeV2({
  phase,
  lines,
  rowsDone,
  headlineLead,
  accountName,
  signedIn,
  onSignOut,
  firstMessage,
  ctaState,
  buttonLabel,
  errorMessage,
  sessionLostAction,
  onComplete,
  onChangeAnswers,
  iconsOn,
}: Step7WelcomeV2Props) {
  const reveal = phase === 'reveal';
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.bar}>
          <span className={styles.brand} aria-hidden="true">
            <TayroMark size={24} />
            <span className={styles.wordmark}>Tayro</span>
          </span>
          {signedIn ? (
            // Fix round 1 (security NOTE C): a sign-out while the completion POST is in
            // flight would let the server stamp completion for the account that just left.
            // The item stays, disabled with its reason, from the tap to the settle (success,
            // failure or lost session). The thinking phase has nothing in flight, so it
            // never disables it.
            <AccountMenu
              name={accountName}
              onSignOut={onSignOut}
              signOutDisabled={reveal && ctaState === 'saving'}
            />
          ) : null}
        </div>
        <div className={styles.chapter}>
          <span className={styles.chapterLabel}>Chapter 4 of 4 · Meeting your coaches</span>
          <div className={styles.segments} aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className={styles.segment}>
                <span
                  className={styles.segmentFill}
                  style={{ width: i === 3 && !reveal ? '50%' : '100%' }}
                />
              </span>
            ))}
          </div>
        </div>
      </header>

      <div className={reveal ? styles.stageReveal : styles.stage}>
        {!reveal ? (
          <div className={styles.thinking} role="status" aria-live="polite">
            <h2 className={styles.title}>Setting up your coaching team…</h2>
            <ol className={styles.rows}>
              {lines.map((line, i) => {
                const state = i < rowsDone ? 'done' : i === rowsDone ? 'active' : 'pending';
                return (
                  <li key={line} className={styles.row} data-thinking-row={line} data-state={state}>
                    <span className={styles.glyph} aria-hidden="true">
                      {state === 'done' ? (
                        <span className={styles.glyphDone}>
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path
                              d="M2.5 6.2l2.2 2.2 4.8-4.8"
                              stroke="var(--paper)"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      ) : state === 'active' ? (
                        <span className={styles.glyphActive} />
                      ) : (
                        <span className={styles.glyphPending} />
                      )}
                    </span>
                    <span className={styles.rowLabel}>{line}</span>
                  </li>
                );
              })}
            </ol>
          </div>
        ) : (
          <div className={styles.reveal}>
            <div className={styles.revealMain}>
              <span className={styles.kicker}>You&rsquo;re in</span>
              <h2 className={styles.title}>
                {headlineLead} coaching team is <em>ready</em>.
              </h2>
              <p className={styles.sub}>
                Five AI coaches, each specializing in one domain. Call any one of them, any time,
                through natural conversation.
              </p>

              <div className={styles.messageCard}>
                <div className={styles.messageHeader}>
                  <span
                    className={styles.messageAvatar}
                    style={{ '--coach-color': LEAD_COACH.token } as CSSProperties & Record<string, string>}
                    aria-hidden="true"
                  >
                    {LEAD_COACH.initial}
                  </span>
                  <span className={styles.messageKicker}>
                    First message · {LEAD_COACH.name} · {LEAD_COACH.landingRole}
                  </span>
                </div>
                <p className={styles.messageBody}>{firstMessage}</p>
              </div>

              <button
                type="button"
                className={styles.cta}
                data-state={ctaState}
                onClick={onComplete}
                disabled={ctaState === 'saving' || ctaState === 'session_lost'}
                aria-busy={ctaState === 'saving'}
              >
                {buttonLabel}
                {iconsOn ? (
                  <Icon name="sys.arrow" size={16} />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path
                      d="M3 7h8m-3-3l3 3-3 3"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>

              {onChangeAnswers ? (
                <button type="button" className={styles.changeAnswers} onClick={onChangeAnswers}>
                  Change my answers
                </button>
              ) : null}

              {(ctaState === 'error' || ctaState === 'session_lost') && errorMessage ? (
                <div className={styles.errorChip} role="alert" aria-live="assertive">
                  <svg
                    className={styles.errorMark}
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle cx="9" cy="9" r="9" fill="currentColor" />
                    <path d="M9 4.8v5" stroke="var(--paper)" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="9" cy="13" r="1.1" fill="var(--paper)" />
                  </svg>
                  <span>
                    {errorMessage}
                    {sessionLostAction ? (
                      <>
                        {' '}
                        <Link to={sessionLostAction.to} className={styles.errorChipAction}>
                          {sessionLostAction.label}
                        </Link>
                      </>
                    ) : null}
                  </span>
                </div>
              ) : null}
            </div>

            <div className={styles.coachColumn}>
              <span className={styles.coachLabel}>Your five coaches</span>
              <div className={styles.coachList}>
                {COACHES.map((c, i) => (
                  <div
                    key={c.id}
                    className={styles.coachRow}
                    style={
                      {
                        '--coach-color': c.token,
                        '--coach-index': String(i),
                      } as CSSProperties & Record<string, string>
                    }
                  >
                    <span className={styles.coachAvatar} aria-hidden="true">
                      {c.initial}
                    </span>
                    <span className={styles.coachHead}>
                      <span className={styles.coachName}>{c.name}</span>
                      <span className={styles.coachRole}>{c.landingRole}</span>
                    </span>
                    <span className={styles.coachDesc}>{c.landingDesc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
